import styled from '@emotion/styled';
import is from '@sindresorhus/is';
import type { ComponentType, PropsWithChildren, ReactNode } from 'react';
import { Fragment, useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import {
    ContextMenuCheckboxItem,
    ContextMenuItem,
    ContextMenuItemIndicator,
    ContextMenuLabel,
    ContextMenuRadioGroup,
    ContextMenuRadioItem,
    ContextMenuSeparator,
} from '~/ui/core/context-menu/ContextMenu';
import {
    DropdownMenuCheckboxItem,
    DropdownMenuItem,
    DropdownMenuItemIndicator,
    DropdownMenuLabel,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuRadioItemContent,
    DropdownMenuSeparator,
} from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconSearch } from '~/ui/core/icons';
import { CheckIcon } from '~/ui/core/icons/CheckIcon';
import { Input } from '~/ui/core/input/Input';

import { AddUserIcon } from './AddUserIcon';

type Reviewer = {
    readonly id: string;
    readonly firstName: string;
    readonly lastName: string;
};

type MenuCheckboxItemProps = {
    readonly checked?: boolean;
    readonly children: ReactNode;
    readonly onCheckedChange?: (checked: boolean) => void;
};

type MenuRadioGroupProps = {
    readonly children: ReactNode;
    readonly onValueChange?: (value: string) => void;
    readonly value?: string;
};

type MenuComponents = {
    readonly MenuCheckboxItem: ComponentType<MenuCheckboxItemProps>;
    readonly MenuCheckboxItemIndicator?: ComponentType<{ readonly children: ReactNode }>;
    readonly MenuItem: ComponentType<{ readonly children: ReactNode; readonly onSelect?: () => void }>;
    readonly MenuLabel: ComponentType<{ readonly children: ReactNode }>;
    readonly MenuRadioGroup: ComponentType<MenuRadioGroupProps>;
    readonly MenuRadioItem: ComponentType<{ readonly children: ReactNode; readonly value: string }>;
    readonly MenuRadioItemContent?: ComponentType<PropsWithChildren>;
    readonly MenuRadioItemIndicator: ComponentType<{ readonly children: ReactNode }>;
    readonly MenuSeparator: ComponentType<Record<string, never>>;
};

type ReviewerMenuItemsProps = {
    readonly currentStatus?: 'approved' | 'assigned';
    readonly onInvite?: () => void;
    readonly onSearch: (searchTerm: string) => void;
    readonly onSelectReviewer: (reviewer: Reviewer) => void;
    readonly onStatusChange?: (status: 'approved' | 'assigned') => void;
    readonly onUnassignReviewer: () => void;
    readonly reviewers: readonly Reviewer[];
    readonly selectedReviewer?: Reviewer;
};

const StyledInnerContent = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
    padding: theme.spacing.xs,
}));

const StyledIcon = styled.div({
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    height: 16,
    justifyContent: 'center',
    width: 16,
});

const StyledMenuItemText = styled.div(({ theme }) => ({
    color: theme.colors.gray[900],
    fontSize: theme.typography.body.S.medium.fontSize,
    fontWeight: theme.typography.body.S.medium.fontWeight,
    letterSpacing: theme.typography.body.S.regular.letterSpacing,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}));

const StatusIcon = styled.div<{ readonly status: 'approved' | 'assigned' }>(({ status, theme }) => ({
    alignItems: 'center',
    backgroundColor: status === 'approved' ? theme.colors.success[600] : theme.colors.gray[400],
    borderRadius: '50%',
    display: 'flex',
    height: theme.spacing.m,
    justifyContent: 'center',
    width: theme.spacing.m,
}));

const WithGap = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    gap: theme.spacing.s,
    minWidth: 0,
}));

const StyledReviewerItemContent = styled.div({
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});

const StyledReviewersList = styled.div({
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 240,
    overflowY: 'auto',
    scrollbarWidth: 'none',
});

const createReviewerMenuItems = (components: MenuComponents): ((props: ReviewerMenuItemsProps) => ReactNode) => {
    const {
        MenuCheckboxItem,
        MenuCheckboxItemIndicator,
        MenuItem,
        MenuLabel,
        MenuRadioGroup,
        MenuRadioItem,
        MenuRadioItemContent,
        MenuRadioItemIndicator,
        MenuSeparator,
    } = components;

    const StyledMenuCheckboxItem = styled(MenuCheckboxItem)({
        display: 'flex',
    });

    const ReviewerMenuItems = ({
        currentStatus = 'assigned',
        onInvite,
        onSearch,
        onSelectReviewer,
        onStatusChange,
        onUnassignReviewer,
        reviewers = [],
        selectedReviewer,
    }: ReviewerMenuItemsProps): ReactNode => {
        const { translate } = useContext(ApplicationContext);
        const statusOptions = [
            {
                icon: <StatusIcon data-testid="review-status-dot" status="assigned" />,
                label: translate('common.enums.job-state.draft'),
                value: 'assigned' as const,
            },
            {
                icon: <StatusIcon data-testid="review-status-dot" status="approved" />,
                label: translate('common.enums.job-profile-review-state.approved'),
                value: 'approved' as const,
            },
        ];

        return (
            <StyledInnerContent style={{ minHeight: '100px' }}>
                <div className="-mx-m -mt-m mb-xs flex items-center gap-m border-b border-border-subtle px-xl text-text-content-subtle">
                    <IconSearch size={16} />
                    <Input
                        className="h-11xl flex-1 min-w-0 text-body-s text-text-content-strong placeholder:text-text-content-subtle"
                        aria-label={translate('pages.job-architecture-v2.search-reviewers-placeholder')}
                        name="search"
                        placeholder={translate('pages.job-architecture-v2.search-reviewers-placeholder')}
                        type="text"
                        variant="ghost"
                        onChange={(e) => onSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                    />
                </div>
                {!is.undefined(onInvite) && (
                    <Fragment>
                        <MenuItem onSelect={onInvite}>
                            <WithGap>
                                <AddUserIcon />
                                {translate('pages.invitation-dialog.title')}
                            </WithGap>
                        </MenuItem>
                        <MenuSeparator />
                    </Fragment>
                )}
                {!is.undefined(onStatusChange) && (
                    <Fragment>
                        <MenuRadioGroup value={currentStatus} onValueChange={onStatusChange}>
                            {statusOptions.map((status) => (
                                <MenuRadioItem key={status.value} value={status.value}>
                                    {is.undefined(MenuRadioItemContent) ? (
                                        <WithGap>
                                            <StyledIcon>{status.icon}</StyledIcon>
                                            <StyledMenuItemText>{status.label}</StyledMenuItemText>
                                        </WithGap>
                                    ) : (
                                        <MenuRadioItemContent>
                                            <WithGap>
                                                <StyledIcon>{status.icon}</StyledIcon>
                                                <StyledMenuItemText>{status.label}</StyledMenuItemText>
                                            </WithGap>
                                        </MenuRadioItemContent>
                                    )}
                                    <MenuRadioItemIndicator>
                                        <CheckIcon />
                                    </MenuRadioItemIndicator>
                                </MenuRadioItem>
                            ))}
                        </MenuRadioGroup>
                        <MenuSeparator />
                    </Fragment>
                )}
                <MenuItem onSelect={onUnassignReviewer}>
                    <WithGap>
                        {translate('pages.job-architecture-v2.job-unassigned')}
                        {is.undefined(selectedReviewer) && <CheckIcon />}
                    </WithGap>
                </MenuItem>
                <MenuSeparator />
                <MenuLabel>{translate('pages.job-architecture-v2.suggested')}</MenuLabel>
                <StyledReviewersList>
                    {reviewers.map((reviewer) => (
                        <StyledMenuCheckboxItem
                            key={reviewer.id}
                            checked={selectedReviewer?.id === reviewer.id}
                            onCheckedChange={() => onSelectReviewer(reviewer)}
                        >
                            <StyledReviewerItemContent>
                                {reviewer.firstName} {reviewer.lastName}
                            </StyledReviewerItemContent>
                            {!is.undefined(MenuCheckboxItemIndicator) && (
                                <MenuCheckboxItemIndicator>
                                    <CheckIcon />
                                </MenuCheckboxItemIndicator>
                            )}
                        </StyledMenuCheckboxItem>
                    ))}
                </StyledReviewersList>
            </StyledInnerContent>
        );
    };

    return ReviewerMenuItems;
};

export const ReviewerDropdownMenuItems = createReviewerMenuItems({
    MenuCheckboxItem: DropdownMenuCheckboxItem,
    MenuItem: DropdownMenuItem,
    MenuLabel: DropdownMenuLabel,
    MenuRadioGroup: DropdownMenuRadioGroup,
    MenuRadioItem: DropdownMenuRadioItem,
    MenuRadioItemContent: DropdownMenuRadioItemContent,
    MenuRadioItemIndicator: DropdownMenuItemIndicator,
    MenuSeparator: DropdownMenuSeparator,
});

export const ReviewerContextMenuItems = createReviewerMenuItems({
    MenuCheckboxItem: ContextMenuCheckboxItem,
    MenuCheckboxItemIndicator: ContextMenuItemIndicator,
    MenuItem: ContextMenuItem,
    MenuLabel: ContextMenuLabel,
    MenuRadioGroup: ContextMenuRadioGroup,
    MenuRadioItem: ContextMenuRadioItem,
    MenuRadioItemIndicator: ContextMenuItemIndicator,
    MenuSeparator: ContextMenuSeparator,
});

export const ReviewerContextMenu = ReviewerDropdownMenuItems;
